"use client";

import React, { useState, useEffect } from "react";
import { ChartEmptyState } from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/ChartEmptyState/ChartEmptyState";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Card/Card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from "recharts";
import { useTheme } from "@/context/ThemeContext";
import { formatCurrency, generateColors } from "../../../utils/chart";
import { ChartMetricCard } from "@/modules/stochastic/features/Reports/SCDashboard/components/ChartMetricCard/ChartMetricCard";
import { TotalSalesNewCustomerByZipCodeAndYearData } from "@/modules/stochastic/features/Reports/SCDashboard/api/fetchStochasticDashboardData/types";
import { ChartBarLegendWithColoredYear } from "@/modules/stochastic/features/Reports/SCDashboard/components/ChartBarLegendWithColoredYear/ChartBarLegendWithColoredYear";
import ChartTooltipWithColoredYears from "@/modules/stochastic/features/Reports/SCDashboard/components/ChartTooltipWithColoredYears/ChartTooltipWithColoredYears";
import { ChartRangeSlider } from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/TotalHouseholdsByZipCodeChart/ChartRangeSlider";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";

interface TotalSalesNewCustomerByZipCodeAndYearChartProps {
  chartId?: string;
  initialData: TotalSalesNewCustomerByZipCodeAndYearData;
}

export default function TotalSalesNewCustomerByZipCodeAndYearChart({
  initialData,
}: TotalSalesNewCustomerByZipCodeAndYearChartProps) {
  if (!initialData?.items || initialData.items.length === 0) {
    return (
      <ChartEmptyState title="Total Sales New Customer by ZIP Code & Year" />
    );
  }

  const { theme } = useTheme();
  const { years, totalCount, minAvgSales, maxAvgSales } = initialData;
  const colors = generateColors(years, theme);

  const [yAxisRange, setYAxisRange] = useState<[number, number]>([
    minAvgSales,
    maxAvgSales,
  ]);
  const [barRange, setBarRange] = useState<[number, number]>([
    1,
    initialData.items.length,
  ]);

  const filteredByYAxis = initialData.items.filter(
    (item) => item.avgSales >= yAxisRange[0] && item.avgSales <= yAxisRange[1],
  );

  useEffect(() => {
    setBarRange([1, filteredByYAxis.length || 1]);
  }, [yAxisRange]);

  const handleYAxisChange = (value: number | [number, number]) => {
    if (Array.isArray(value)) {
      setYAxisRange(value);
    }
  };

  const handleBarRangeChange = (value: number | [number, number]) => {
    if (Array.isArray(value)) {
      setBarRange(value);
    }
  };

  const effectiveEnd = Math.min(barRange[1], filteredByYAxis.length);
  const displayData = filteredByYAxis.slice(barRange[0] - 1, effectiveEnd);

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-fontColor dark:text-light">
            Total Sales New Customer by ZIP Code & Year
          </CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 shadow-lg">
                <p>
                  Shows how market capture is expanding or contracting in
                  specific neighborhoods.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          New customer revenue by ZIP codes over time
        </p>
      </CardHeader>

      <CardContent>
        <div className="flex">
          <div className="flex items-stretch justify-start mr-4">
            <ChartRangeSlider
              className="h-full"
              max={maxAvgSales}
              min={minAvgSales}
              onChange={handleYAxisChange}
              orientation="vertical"
              tooltipFormatter={formatCurrency}
              tooltipLabel="Avg Sales ($)"
              value={yAxisRange}
            />
          </div>

          <div className="flex-1">
            <ResponsiveContainer height={500} width="100%">
              <BarChart
                data={displayData}
                margin={{ top: 20, right: 30, left: 45, bottom: 5 }}
              >
                <CartesianGrid
                  stroke={theme === "dark" ? "#374151" : "#e5e7eb"}
                  strokeDasharray="3 3"
                />
                <XAxis
                  angle={-90}
                  dataKey="postalCode"
                  fontSize={14}
                  height={100}
                  stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
                  textAnchor="end"
                />
                <YAxis
                  stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
                  tickFormatter={formatCurrency}
                />
                <ChartTooltip
                  content={
                    <ChartTooltipWithColoredYears
                      colors={colors}
                      labelKey="postalCode"
                      labelPrefix="ZIP:"
                      valueSuffix=""
                    />
                  }
                />
                <Legend content={<ChartBarLegendWithColoredYear />} />
                <ReferenceLine stroke="#ccc" y={0} />
                {years.map((year) => (
                  <Bar
                    dataKey={year}
                    fill={colors[year]}
                    isAnimationActive={true}
                    key={year}
                    name={year}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>

            <div className="mt-8 px-2">
              <ChartRangeSlider
                max={filteredByYAxis.length}
                min={1}
                onChange={handleBarRangeChange}
                orientation="horizontal"
                value={[
                  barRange[0],
                  Math.min(barRange[1], filteredByYAxis.length),
                ]}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-6 mt-6">
          <ChartMetricCard
            label="Bars Shown"
            value={`${barRange[0]} – ${effectiveEnd}`}
          />
          <ChartMetricCard label="Visible ZIPs" value={displayData.length} />
          <ChartMetricCard label="Total ZIPs" value={totalCount} />
          <ChartMetricCard
            label="Years Tracked"
            value={`${years[0]}–${years.at(-1)}`}
          />
        </div>
      </CardContent>
    </Card>
  );
}
